import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Selection } from 'react-aria-components'

import type { ImportIngredientPayload } from '@/api/types'
import { fetchIngredients, importIngredient, ingredientsQuery } from '@/api'
import { Button } from '@/components/Button'
import { CompactTable } from '@/components/Table'
import { ModalContent, ModalFooter } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'

import { Label } from '@/components/Label'
import { Select } from '@/components/Select'
import { Flex } from '@/components/Flex'
import { toast } from 'react-toastify'
import { useRouter } from '@tanstack/react-router'
import { contextsQuery } from '@/api/contexts'

type Props = {
  close: () => void
}

export default function FCDBIngredientsImporterPanel({ close }: Props) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set())
  const router = useRouter()
  const { data: contexts } = useQuery(contextsQuery)
  const [database, setDatabase] = useState('')

  useEffect(() => {
    if (contexts && contexts.length > 0) {
      setDatabase(contexts[0])
    }
  }, [contexts])

  const { data = [], isLoading: ingredientsLoading } = useQuery({
    queryKey: ['ingredientsSearch', { country: database }],
    queryFn: () => fetchIngredients({ country: database }),
    enabled: !!database,
  })

  const mutation = useMutation({
    mutationFn: (formData: ImportIngredientPayload) => importIngredient(formData),
    onSuccess: (res) => {
      if (res.status === 200) {
        queryClient.removeQueries(ingredientsQuery())
        toast.success('Ingredient imported with success')
        router.navigate({
          to: '/ingredients/fcdb/$ingId',
          params: {
            ingId: res.data.idIngredient!.toString(),
          },
        })
        close()
      } else if (res.status === 409) {
        toast.error('Ingredient already exists')
      } else {
        toast.error('There was an error importing')
      }
    },
  })

  return (
    <>
      <ModalContent
        style={{
          maxHeight: '100%',
        }}>
        <Flex direction="column" gap={30} style={{ alignItems: 'stretch' }}>
          {ingredientsLoading ? (
            <div style={{ minHeight: 400 }}>
              <Spinner />
            </div>
          ) : (
            <>
              {' '}
              <div>
                <Label>{t('database')}</Label>
                <Select
                  getOptionLabel={(option) =>
                    option?.name ? t(`countries.${option.name}` as any) : ''
                  }
                  getOptionValue={(option) => option.name}
                  onChange={(selection) => (selection ? setDatabase(selection.name) : null)}
                  value={{ name: database }}
                  options={contexts?.map((country) => ({ name: country }))}
                />
              </div>
              <CompactTable
                aria-label="FCDB ingredients"
                selectionMode="single"
                onSelectionChange={setSelectedKeys}
                rowId="idIngredient"
                sorting
                search={{
                  columns: ['name', 'englishName'],
                }}
                columns={[
                  {
                    key: 'idIngredient',
                    label: 'ID',
                    width: 100,
                  },
                  {
                    key: 'name',
                    label: t('ingredient.name'),
                  },
                  {
                    key: 'englishName',
                    label: t('ingredient.englishName'),
                  },
                ]}
                rows={data}
              />
            </>
          )}
        </Flex>
      </ModalContent>
      <ModalFooter>
        <Button
          onPress={() => {
            close()
          }}>
          {t('cancelButton')}
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            mutation.mutate({
              idIngredient: Array.from(selectedKeys)[0] as number,
              country: database,
            })
          }}>
          {t('ingredient.importIngredient')}
        </Button>
      </ModalFooter>
    </>
  )
}
